import {
    useGetConsentLookupsQuery,
    useGetMedicalConsentOptionsQuery,
    useManageMedicalConsentsMutation
} from '../../../../api/services/lookupsService';
import {Alert, Box, Button} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {useEffect, useRef, useState} from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import EditIcon from '@mui/icons-material/Edit';
import useNotification from '../../../../utility/hooks/useNotification';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import Stack from '@mui/material/Stack';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import Divider from '@mui/material/Divider';
import PropTypes from 'prop-types';
import Uploader from '../../../SchoolManagement/Setup/Uploader';
import {
    downloadConsentFile,
    useDeleteConsentFileMutation,
    useUploadConsentFileMutation
} from '../../../../api/services/filesService';
import objectToFormData from '../../../../utility/objectToFormData';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import {useConfirm} from 'material-ui-confirm';
import CircularProgress from '@mui/material/CircularProgress';
import {
    useGetProfileDescriptionsQuery,
    useUpdateProfileDescriptionMutation
} from '../../../../api/services/schoolManagementService';
import MultilineTextField from '../../../../shared-components/MultilineTextField';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';

const Lookups = () => {
    const {data: medicalConsentOptions = {options: []},} = useGetMedicalConsentOptionsQuery();
    const {data: consents = [],} = useGetConsentLookupsQuery();
    const {
        data: descriptionData = [],
        isError: isLoadingDescriptionsFailed
    } = useGetProfileDescriptionsQuery();
    const [updateProfileDescription, {isError: isUpdateProfileDescriptionError}] = useUpdateProfileDescriptionMutation();
    const {showErrorNotification} = useNotification();

    useEffect(() => {
        if (isUpdateProfileDescriptionError) {
            showErrorNotification('Failed to update description');
        }

        if (isLoadingDescriptionsFailed) {
            showErrorNotification('Failed to load profile descriptions');
        }
    }, [isUpdateProfileDescriptionError, isLoadingDescriptionsFailed]);

    const handleDescriptionChange = async (key, value) => {
        await updateProfileDescription({key, value});
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            sx={{
                pt: 2,
                width: {
                    xs: '100%',
                },
                maxWidth: 1200,
                mx: 'auto',
            }}
            gap={2}
        >
            <DescriptionSettings
                header={'Medical conditions'}
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.medicalConditions)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.medicalConditions]
                }
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.medicalConditions]}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.medicalConditions, value)}
            />
            <DescriptionSettings
                header={'SEN'}
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.sen)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.sen]
                }
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.sen]}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.sen, value)}
            />
            <DescriptionSettings
                header={'Passport'}
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.passport)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.passport]
                }
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.passport]}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.passport, value)}
            />
            <DescriptionSettings
                header={'Vaccination'}
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.vaccination)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.vaccination]
                }
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.vaccination]}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.vaccination, value)}
            />
            <DescriptionSettings
                header={'Contacts'}
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.contacts)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.contacts]
                }
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.contacts]}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.contacts, value)}
            />
            <ConsentsSettings
                consents={consents}
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.parentalConsents)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.parentalConsents]
                }
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.parentalConsents, value)}
            />
            <Divider/>
            <MedicalConsentSettings
                medicalConsentOptions={medicalConsentOptions}
                title={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.otcMedicationTitle)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.otcMedicationTitle]
                }
                description={descriptionData
                    .find(x => x.key === PROFILE_SECTIONS_KEYS.otcMedication)
                    ?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.otcMedication]
                }
                onTitleChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.otcMedicationTitle, value)}
                onDescriptionChange={value => handleDescriptionChange(PROFILE_SECTIONS_KEYS.otcMedication, value)}
            />
        </Box>
    );
};

function DescriptionSettings(
    {
        header,
        description,
        defaultDescription,
        onDescriptionChange,
        fieldLabel = 'Description',
        saveButtonText = 'Save description'
    }) {
    const [text, setText] = useState(description);
    const actions = text !== defaultDescription && defaultDescription
        ? [
            {
                label: 'Revert to Default Description',
                onClick: () => setText(defaultDescription)
            }
        ]
        : [];

    useEffect(() => {
        setText(description);
    }, [description]);

    return (
        <Box>
            {header
                ? <Typography variant="h6" fontWeight="bold" gutterBottom>{header}</Typography>
                : null
            }
            <MultilineTextField
                label={fieldLabel}
                value={text}
                onChange={setText}
                actions={actions}
            />
            {description !== text && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onDescriptionChange(text)}
                    sx={{
                        marginTop: 1,
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                >
                    {saveButtonText}
                </Button>
            )}
        </Box>
    );
}

DescriptionSettings.propTypes = {
    header: PropTypes.string,
    description: PropTypes.string,
    defaultDescription: PropTypes.string,
    onDescriptionChange: PropTypes.func,
    fieldLabel: PropTypes.string,
    saveButtonText: PropTypes.string
};

function ConsentsSettings(
    {
        consents,
        description,
        onDescriptionChange
    }) {
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [selectedConsent, setSelectedConsent] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const [uploadFile, {isLoading: isFileUploading, isError: isUploadError}] = useUploadConsentFileMutation();
    const [deleteFile, {isError: isDeleteError}] = useDeleteConsentFileMutation();

    const downloadFile = useFileDownloader();
    const {showErrorNotification} = useNotification();
    const confirm = useConfirm();

    useEffect(() => {
        if (isUploadError) {
            showErrorNotification('Failed to upload consent document');
        }
        if (isDeleteError) {
            showErrorNotification('Failed to delete consent document');
        }
    }, [isUploadError, isDeleteError]);

    const handleDownloadFile = async (consentCode) =>
        await downloadFile(
            downloadConsentFile,
            consentCode);

    const closeUploadDialog = () => {
        setOpenUploadDialog(false);
        setSelectedFiles([]);
        setIsUploadSizeExceedsLimit(false);
        setSelectedConsent(null);
    };

    const handleFileUpload = async () => {
        if (selectedFiles.length === 0) {
            return;
        }

        await uploadFile(objectToFormData({
            file: selectedFiles[0],
            consentCode: selectedConsent.code
        }));
        closeUploadDialog();
    };

    const handleFileDelete = async consentCode =>
        confirm({
            title: 'Confirm Deletion',
            description:
                'Are you sure you want to delete the consent document? Once deleted, it cannot be recovered.',
            confirmationText: 'Delete',
            cancellationText: 'Cancel',
            confirmationButtonProps: {
                sx: {
                    color: '#186BBE',
                },
            },
            cancellationButtonProps: {
                sx: {
                    colour: '#186BBE',
                },
            },
        })
            .then(async () => await deleteFile({consentCode}))
            .catch(_ => {
            });

    return (
        <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Parental Consents</Typography>
            <DescriptionSettings
                description={description}
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.parentalConsents]}
                onDescriptionChange={value => onDescriptionChange(value)}
            />
            <List sx={{width: '100%'}}>
                {
                    [...consents]
                        ?.sort((a, b) => a.description.localeCompare(b.description))
                        .map((consent, index) => (
                            <ListItem key={`consent-${index}`} disablePadding>
                                {
                                    consent.file != null
                                        ? <ListItemText
                                            primary={consent.description}
                                            secondary={
                                                <Stack
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                >
                                                    <Stack flexDirection="row" alignItems="center" gap={1}>
                                                        <InsertDriveFileRoundedIcon
                                                            fontSize="small"
                                                            sx={{color: 'primary.main'}}
                                                        />
                                                        <Typography variant="body2">{consent.file.name}</Typography>
                                                    </Stack>
                                                    <Stack flexDirection="row">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDownloadFile(consent.code)}
                                                        >
                                                            <DownloadRoundedIcon fontSize="small"/>
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                color: 'red',
                                                                ':hover': {
                                                                    color: 'error.light'
                                                                }
                                                            }}
                                                            onClick={() => handleFileDelete(consent.code)}
                                                        >
                                                            <DeleteOutlineRoundedIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Stack>
                                                </Stack>
                                            }/>
                                        : <ListItemText
                                            primary={consent.description}
                                            secondary={
                                                <Link
                                                    sx={{
                                                        color: '#186BBE',
                                                        textDecoration: 'underline',
                                                        textDecorationColor: '#186BBE',
                                                        textUnderlineOffset: '2px',
                                                        '&:hover': {
                                                            textDecorationColor: '#155FA7',
                                                        },
                                                    }}
                                                    component="button"
                                                    onClick={() => {
                                                        setSelectedConsent(consent);
                                                        setOpenUploadDialog(true);
                                                    }}
                                                >Upload document</Link>
                                            }/>
                                }
                            </ListItem>
                        ))
                }
            </List>
            <AdmicityDialog
                handleClose={closeUploadDialog}
                title={'Add Documents'}
                open={openUploadDialog}
                actions={[
                    {
                        label: 'Cancel',
                        onClick: closeUploadDialog
                    },
                    {
                        label: 'Upload',
                        onClick: handleFileUpload,
                        startIcon: isFileUploading ? <CircularProgress color="inherit" size={16}/> : undefined,
                        disabled: selectedFiles?.length === 0 || isUploadSizeExceedsLimit || isFileUploading,
                    }
                ]}
                maxWidth={'md'}
            >
                <Uploader
                    totalUploadSizeInMb={25}
                    onFilesSelect={setSelectedFiles}
                    onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                    singleFile
                    value={selectedFiles}
                />
            </AdmicityDialog>
        </Box>
    );
}

ConsentsSettings.propTypes = {
    consents: PropTypes.array,
    description: PropTypes.string,
    onDescriptionChange: PropTypes.func
};

function MedicalConsentSettings({
    medicalConsentOptions,
    title,
    description,
    onTitleChange,
    onDescriptionChange
}) {
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const formRef = useRef(null);
    const [selectedConsent, setSelectedConsent] = useState({});
    const [activeConsents, setActiveConsents] = useState([]);
    const {showErrorNotification, showSuccessNotification} = useNotification();

    const [manageConsents, {
        isSuccess,
        isError,
    }] = useManageMedicalConsentsMutation();

    useEffect(() => {
        setActiveConsents(medicalConsentOptions?.options);
    }, [medicalConsentOptions]);

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed to update medical consents');
        } else if (isSuccess) {
            showSuccessNotification(
                'Medical consents updated successfully. The Groups filter tree will reflect these changes after the next MIS synchronization.'
            );
        }
    }, [isSuccess, isError]);

    const textFields = [{
        label: 'Description',
        name: 'description',
        required: true,
        initialValue: selectedConsent?.description ?? undefined,
        validate: (value) => {
            return {
                isValid: value && value.length <= 300,
                message: getValidationMessage(value)
            };
        }
    }];

    const getValidationMessage = (value) => {
        if (value.length > 0 && value.length <= 300) {
            return '';
        }

        return value.length === 0
            ? 'Please provide a description.'
            : 'Description cannot exceed 300 characters.';
    };

    const deleteConsent = (description) => {
        setActiveConsents(activeConsents.filter(e => e.description !== description));
    };

    const handleAdd = async (payload) => {
        setActiveConsents([...activeConsents, {description: payload.description, id: null}]);
        handleFinishEditing();
    };

    const startEditing = (consent) => {
        setSelectedConsent(consent);
        setOpenEditDialog(true);
    };

    const handleEdit = async (payload) => {
        const newConsents = activeConsents.map(consent => {
            if (consent.description === selectedConsent.description) {
                return {
                    ...consent,
                    description: payload.description
                };
            }
            return consent;
        });

        setActiveConsents(newConsents);
        handleFinishEditing();
    };

    const handleFinishEditing = () => {
        setOpenAddDialog(false);
        setOpenEditDialog(false);
        setSelectedConsent(undefined);
    };

    return (
        <Box>
            <Typography variant="h6" fontWeight="bold">Medical Consents</Typography>
            <DescriptionSettings
                description={title}
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.otcMedicationTitle]}
                onDescriptionChange={onTitleChange}
                fieldLabel="Heading"
                saveButtonText="Save heading"
            />
            <DescriptionSettings
                description={description}
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.otcMedication]}
                onDescriptionChange={onDescriptionChange}
            />
            <Alert severity="info" sx={{mt: 2}}>
                Changes to Medical OTC consent options are saved here immediately. The Groups filter tree
                will be refreshed with the updated values after the next MIS synchronization.
            </Alert>
            <Box marginTop={2} marginBottom={2}>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    onClick={() => setOpenAddDialog(true)}
                >
                    Add new
                </Button>
            </Box>
            <List sx={{width: '100%'}}>
                {
                    activeConsents.map((entity, index) =>
                        <ListItem
                            key={`medicalConsent-${index}`}
                            sx={{
                                p: 1,
                                backgroundColor: index % 2 === 0 ? '#F0FBFA' : '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#D4F4F0',
                                },
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Stack
                                        flexDirection="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography>{entity?.description}</Typography>
                                        <Box display={'flex'}>
                                            <IconButton size="small" onClick={() => startEditing(entity)}>
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton size="small"
                                                        onClick={() => deleteConsent(entity.description)}>
                                                <DeleteIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                }
                            />
                        </ListItem>)
                }
            </List>
            <Box display={'flex'} justifyContent={'end'} py={2}>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                        onClick={() => manageConsents({activeMedicalConsents: activeConsents})}>
                    Save
                </Button>
            </Box>
            <AdmicityDialog
                handleClose={handleFinishEditing}
                title={openAddDialog ? 'New Medical Consent' : 'Update Medical Consent'}
                open={openAddDialog || openEditDialog}
                actions={[]}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={(data) => openAddDialog
                        ? handleAdd(data)
                        : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
}

MedicalConsentSettings.propTypes = {
    medicalConsentOptions: PropTypes.object,
    title: PropTypes.string,
    description: PropTypes.string,
    onTitleChange: PropTypes.func,
    onDescriptionChange: PropTypes.func
};

export default Lookups;
